import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ListItemRendererComponent } from 'gung-list';
import { GroupedBlockOrderAgreement, OttoOlsenBlockOrderAgreementsListConfigService } from '../../services/otto-olsen-block-order-agreements-list-config.service';
import { OttoOlsenBlockOrder, OttoOlsenBlockOrderAgreementsService, SigningStatus } from '../../services/otto-olsen-block-order-agreements.service';
import { AuthService, AvailabilityService, DownloadFileService, GungModalService, MetadataService, ProductService } from 'gung-standard';
import { first, forkJoin } from 'rxjs';
import { Router } from '@angular/router';
import { PresignedUrlRequest, S3FileRelation, S3UploadService } from 'gung-common';
import { TranslateService } from '@ngx-translate/core';
import { OttoOlsenEmailSigningModalComponent } from '../otto-olsen-email-signing-modal/otto-olsen-email-signing-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

interface BlockOrderAgreementType {
  id: number;
  name: string;
  groupedOrder: GroupedBlockOrderAgreement[]
}
@Component({
  selector: 'otto-olsen-block-order-table-list',
  templateUrl: './otto-olsen-block-order-table-list.component.html',
  styleUrl: './otto-olsen-block-order-table-list.component.scss'
})
export class OttoOlsenBlockOrderTableListComponent extends ListItemRendererComponent<GroupedBlockOrderAgreement[]> implements OnInit, OnChanges {

  customerDetails = true;
  showGroup: { [k: string]: boolean } = {};
  orderTypes = [970, 971, 972, 973];

  signingStatus: { [k: string]: SigningStatus } = {}

  headerSort = {
    agreementId: null,
    customerId: null,
    offbeskr: null,
    status: null,
    contact: null,
    refNumber: null,
    agreementDate: null,
    agreementValidToDate: null
  }

  isAdmin = false;

  isSales = false;

  hasAccessToCreate = false

  orders: BlockOrderAgreementType[] = [];
  uploading = {};

  constructor(
    protected availabilityService: AvailabilityService,
    protected authService: AuthService, protected productService: ProductService,
    protected router: Router,
    protected metadataService: MetadataService,
    protected blockOrderAgreementsService: OttoOlsenBlockOrderAgreementsService,
    protected ottoOlsenBlockOrderAgreementsListConfigService: OttoOlsenBlockOrderAgreementsListConfigService,
    protected downloadFileService: DownloadFileService,
    protected s3Upload: S3UploadService,
    protected gungModalService: GungModalService,
    protected translateService: TranslateService,
    protected ngbModal: NgbModal
  ) {
    super()
  }


  ngOnInit(): void {

    this.authService.getRoles().pipe(first()).subscribe(roles => {
      this.isSales = roles.includes('SALES');
      this.isAdmin = roles.includes('ADMIN')
    });
    if (!this.router.url.includes('customer')) {
      this.customerDetails = false;
    }
    this.authService.getCurrentUser().pipe(first()).subscribe(user => {
      this.hasAccessToCreate = user.extra.createBlockOrders
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.orders = [];
    for (const type of this.orderTypes) {
      const groupedOrder = this.data.filter(d => d.orderType === type);
      if (groupedOrder.length > 0) {
        const orders: BlockOrderAgreementType = {
          id: type,
          name: this.metadataService.getMetadataValue('x6', 'ordtypbeskr', type.toString()),
          groupedOrder: groupedOrder
        }

        for (const go of groupedOrder) {
          if (!this.signingStatus?.[go?.agreementId]) {
            for (const r of go.rows) {
              if (r.extra.signingStatus === 'SIGNED') {
                this.signingStatus[go.agreementId] = r.extra.signingStatus;
                break;
              }
            }
          }
        }

        this.orders.push(orders);
      }
    }
  }


  toggleLines(group: GroupedBlockOrderAgreement) {
    this.showGroup[group.agreementId] = !this.showGroup[group.agreementId];
    const getInfoIds = [];
    for (const row of group.rows) {
      if (!row.extra?._av) {
        getInfoIds.push(row.extra.q_gung_ofh.artnr);
      }
    }
    if (getInfoIds && getInfoIds.length > 0) {
      forkJoin({
        products: this.productService.getFullProductsByIds(getInfoIds).pipe(first()),
        avs: this.availabilityService.getAvailabilities(getInfoIds).pipe(first())
      }).subscribe(({ products, avs }) => {
        for (const av of avs) {
          group.rows.find(r => r.extra.q_gung_ofh.artnr === av.productId).extra._av = av;
        }
        for (const p of products) {
          group.rows.find(r => r.extra.q_gung_ofh.artnr === p.id).extra._product = p;
        }
      })
    }
  }

  getPriceWithDiscount(row: OttoOlsenBlockOrder) {
    let price = row.extra.q_gung_ofh?.vb_pris || 0.0;
    let discountPercentage = row.extra.q_gung_ofh?.rabatt1 || 0.0;

    let discountFactor = 1 - discountPercentage / 100;
    return price * discountFactor;
  }

  download(id) {
    this.blockOrderAgreementsService.downloadDocument(id).subscribe(_ => {
    })
  }

  generateRandomString(length: number): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    let result = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters[randomIndex];
    }
    return result;
  }

  signing(group) {
    const email = group.rows[0]?.extra?.q_gung_ofh?.email1 || '';
    this.openEmailSigningModal(email).then(res => {
      if (res?.email) {
        this.blockOrderAgreementsService.signing(res.email, group.agreementId).subscribe(r => {
          this.gungModalService.openConfirmYesNoModal(
            this.translateService.instant('SENT'),
            this.translateService.instant('SENT_SIGNED_TEXT'),
            {
              size: 'sm'
            },
            'OK',
            null
          )
        })
      }
    })
  }

  openEmailSigningModal(email) {
    const id = OttoOlsenEmailSigningModalComponent.name;

    const ref = this.ngbModal.open(OttoOlsenEmailSigningModalComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: true
    });
    ref.componentInstance.delegate = this;
    ref.componentInstance.email = email;

    return ref.result;
  }


  upload(event: any, group: GroupedBlockOrderAgreement) {
    this.uploading[group.agreementId] = true;
    const randon = this.generateRandomString(8);
    let request: PresignedUrlRequest = {
      key: `block-order-agreements/${group.customerId}/${randon}/${group.agreementId}`,
      appendCustomerPrefix: true
    }
    this.s3Upload.presignedUrlUpload(request).subscribe(s3Url => {
      const file = event.target.files[0];
      this.s3Upload.uploadFile(file, s3Url.url).subscribe(etag => {

        const payload: S3FileRelation = {
          s3Uri: request.key,
          relationIds: [
            "ottoBlockOrder:" + group.agreementId
          ],
          documentType: 'blockOrderContract',
          s3Etag: etag
        }
        this.s3Upload.createFileRelation(payload).subscribe(done => {
          this.uploading[group.agreementId] = false;
          this.gungModalService.openConfirmYesNoModal('', this.translateService.instant('UPLOAD_COMPLETED'), {}, 'OK', null).then();
        });

      })
    })
  }



  sortColumn(headerKey) {
    const ascSort = !this.headerSort[headerKey] || this.headerSort[headerKey] === 'asc' ? -1 : 1;
    for (let key in this.headerSort) {
      if (this.headerSort.hasOwnProperty(key)) {
        this.headerSort[key] = null;
      }
    }
    this.headerSort[headerKey] = ascSort === 1 ? 'asc' : 'desc';
    this.ottoOlsenBlockOrderAgreementsListConfigService.selectedColumnSort.next({ field: headerKey, sort: this.headerSort[headerKey] })
  }


  checkSigningStatus(id) {
    this.blockOrderAgreementsService.signingStatus(id).pipe(first()).subscribe(s => {
      this.signingStatus[id] = s.status;
    })
  }
}
